import React from "react";
import { ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import PandaWave from "./PandaWave";
import Illustration from "../../../../../assets/meeting/Remote Meeting.png";
import defaultUserAvatar from "../../../../../assets/Panda_headshot.png";

// Toolbar Icons
import UnmuteIcon from "../../../../../assets/meeting/Unmute.png";
import StartVideoIcon from "../../../../../assets/meeting/Start Video.png";
import SecurityShieldIcon from "../../../../../assets/meeting/Security.png";
import ParticipantsIcon from "../../../../../assets/meeting/Participants.png";
import ChatIcon from "../../../../../assets/meeting/Chat.png";
import ShareScreenIcon from "../../../../../assets/meeting/Share Screen.png";
import PollingIcon from "../../../../../assets/meeting/Polling.png";
import RecordIcon from "../../../../../assets/meeting/Record.png";
import BreakoutIcon from "../../../../../assets/meeting/Breakout.png";
import ReactionIcon from "../../../../../assets/meeting/Reaction.png";

import { progressService } from "../../../../../services/progressService.js";

const OPTIONS = [
  { label: "A", title: "Passive", desc: "Mostly silent in meetings" },
  { label: "B", title: "Low Attention", desc: "Mind drifted off during discussions" },
  { label: "C", title: "Back bencher", desc: "Stayed out of view; camera off or unseen" },
  { label: "D", title: "Eager Accepter", desc: "Said 'yes' to new tasks too easily" },
  { label: "E", title: "Dominant Speaker", desc: "Overtalked others unintentionally" },
  { label: "F", title: "Distracted by Multitasking", desc: "Switched to other tabs/apps" },
  { label: "G", title: "Frequent Interruptions", desc: "Interrupted without noticing" },
  { label: "H", title: "Anxious", desc: "Felt nervous speaking up" },
  { label: "I", title: "Idea-Hesitant", desc: "Held back ideas" },
  { label: "J", title: "Fast Agree-er", desc: "Agreed quickly to avoid conflict" },
  { label: "K", title: "Camera Conscious", desc: "Self-conscious appearing on video" },
  { label: "L", title: "Zoner", desc: "Mind wandered often" },
  { label: "M", title: "Overlistener", desc: "Listened but rarely participated" },
  { label: "N", title: "Delayed Responder", desc: "Struggled to form responses quickly" },
  { label: "O", title: "Social Avoider", desc: "Avoided meeting interactions" },
];

const QuestionPages = ({
  options,
  onSelectionChange,
  defaultSelected = [],
  onPageChange,
  onRegisterPageNav = () => {},
}) => {
  const pageSize = 4;
  const totalPages = Math.ceil(options.length / pageSize);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [selected, setSelected] = React.useState(defaultSelected);

  // Hydrate selected when parent loads saved data
  React.useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  // Jump to correct page on load
  React.useEffect(() => {
    if (defaultSelected.length === 0) return;

    const firstIndex = options.findIndex(opt =>
      defaultSelected.includes(opt.title)
    );

    if (firstIndex >= 0) {
      const page = Math.floor(firstIndex / pageSize);
      setCurrentPage(page);
    }
  }, [defaultSelected, options]);

  React.useEffect(() => {
    onPageChange?.(currentPage, totalPages);
  }, [currentPage, totalPages, onPageChange]);

  const pageOptions = options.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize
  );

  const next = () =>
    currentPage < totalPages - 1 && setCurrentPage(currentPage + 1);

  const prev = () =>
    currentPage > 0 && setCurrentPage(currentPage - 1);

  const goToPage = React.useCallback(
    (page) => {
      if (page >= 0 && page < totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  React.useEffect(() => {
    onRegisterPageNav(goToPage);
    return () => onRegisterPageNav(() => {});
  }, [goToPage, onRegisterPageNav]);

  const toggleSelect = (title) => {
    setSelected((prev) => {
      const updated = prev.includes(title)
        ? prev.filter((x) => x !== title)
        : [...prev, title];

      onSelectionChange(updated);
      return updated;
    });
  };
  return (
    <div className="relative mt-6">
      {/* LEFT ARROW */}
      {currentPage > 0 && (
        <button
          onClick={prev}
          className="absolute -left-8 top-[140px] bg-white shadow p-2 rounded-full border"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* RIGHT ARROW */}
      {currentPage < totalPages - 1 && (
        <button
          onClick={next}
          className="absolute -right-8 top-[140px] bg-white shadow p-2 rounded-full border"
        >
          <ChevronRight size={18} />
        </button>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, x: 25 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -25 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col gap-4"
        >
          {pageOptions.map((opt) => (
            <div
              key={opt.title}
              onClick={() => toggleSelect(opt.title)}
              className={`
                w-[496px] h-[70px]
                border rounded-xl flex items-center px-4 gap-4 cursor-pointer transition
                bg-white
                ${selected.includes(opt.title) ? "border-[#54B0AF]" : "border-[#CCCCCC]"}
              `}
            >
              {/* Label A/B/C */}
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EFEFEF] text-gray-700 font-semibold">
                {opt.label}
              </div>

              <div className="flex-1">
                <p className="font-semibold text-[15px] leading-tight">{opt.title}</p>
                <p className="text-gray-500 text-[13px] -mt-[1px] leading-tight">
                  {opt.desc}
                </p>
              </div>

              {/* Checkbox */}
              <div
                className={`
                  w-5 h-5 rounded-md border flex items-center justify-center
                  ${
                    selected.includes(opt.title)
                      ? "bg-[#54B0AF] border-[#54B0AF]"
                      : "border-[#CCCCCC] bg-white"
                  }
                `}
              >
                {selected.includes(opt.title) && (
                  <span className="text-white text-xs font-bold">✓</span>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

    </div>
  );
};

const MyBeliefs = ({
  userId,
  subsectionId,
  onNextStep,
  user = { name: "You", avatar: defaultUserAvatar },
}) => {
  const [selectedBehaviors, setSelectedBehaviors] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const pageNavRef = React.useRef(null);

  const registerPageNav = React.useCallback((fn) => {
    pageNavRef.current = fn;
  }, []);

React.useEffect(() => {
  async function loadSaved() {
    try {
      const saved = await progressService.getUserProgress(userId, subsectionId);

      // saved value depends on how you stored it:
      const prev = saved?.data?.meeting_behaviors || [];

      setSelectedBehaviors(prev);  // hydrate parent state

      // Also inform QuestionPages
      setInitialLoad(prev);
    } catch (e) {
      console.error("Failed to load saved behaviors", e);
    }
  }

  loadSaved();
}, [userId, subsectionId]);

  const handleFinish = async () => {
    try {
      setSaving(true);

      await progressService.saveProgress(
        userId,
        subsectionId,
        {
          meeting_behaviors: selectedBehaviors,  // dY"ť saved in DB
        },
        "completed"
      );

      onNextStep();  // move to next step
    } catch (err) {
      console.error("Failed to save behaviors", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full h-full bg-[#EAEFF4] flex items-center justify-center">
      <div className="w-full max-w-6xl bg-black rounded-xl border border-[#3BA7FF] overflow-hidden flex flex-col">

        {/* TOP BAR */}
        <div className="w-full bg-[#2B2B2B] px-4 py-2 flex items-center relative">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
            <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
            <div className="w-3 h-3 rounded-full bg-[#28C840]" />
          </div>

          <div className="absolute left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-2 bg-[#1E1E1E] border border-[#3A3A3A] px-3 py-[6px] rounded-md text-[#DADADA] text-sm cursor-pointer">
              Original Sound: Off
              <ChevronDown size={16} />
            </div>
          </div>

          <button className="ml-auto bg-[#1E1E1E] border border-[#3A3A3A] px-3 py-[6px] rounded-md text-[#DADADA] text-sm">
            View
          </button>
        </div>

        {/* MAIN */}
        <div className="flex w-full h-full bg-white">

        {/* LEFT PANE */}
          <div className="flex w-[70%] p-10">

            {/* Illustration EXACT Figma position */}
            <div className="w-[260px] flex items-start justify-center mt-6 mr-12">
              <img
                src={Illustration}
                alt="illustration"
                className="w-[242px] h-[242px] object-contain"
              />
            </div>

            {/* Questions */}
            <div className="flex-1 flex flex-col">
              <div>
                <h2 className="text-3xl font-semibold text-black leading-snug">
                  Which of these behaviors do you exhibit in meetings?
                </h2>

                <p className="text-gray-500 mt-1 mb-6 text-sm">
                  Select options that apply. Scroll to see more options.
                </p>

                <QuestionPages
                  options={OPTIONS}
                  onSelectionChange={setSelectedBehaviors}
                  defaultSelected={initialLoad}
                  onPageChange={(page, pages) => {
                    setCurrentPage(page);
                    setTotalPages(pages);
                  }}
                  onRegisterPageNav={registerPageNav}
                />
              </div>

              <div className="mt-6 flex flex-col gap-4">
                <div className="flex justify-center gap-2">
                  {Array.from({ length: totalPages }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => pageNavRef.current?.(idx)}
                      className={`w-3 h-3 rounded-full transition ${
                        currentPage === idx ? "bg-[#54B0AF]" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>

                <div className="flex justify-start">
                  <button
                    onClick={() => {
                      if (currentPage < totalPages - 1) {
                        // "Continue" - move to next page
                        pageNavRef.current?.(currentPage + 1);
                      } else {
                        // "Finish Selection" - save and navigate
                        handleFinish();
                      }
                    }}
                    disabled={saving}
                    className={`px-6 py-3 text-white font-gotham font-medium rounded-full flex items-center gap-2 text-sm transition ${
                      selectedBehaviors.length > 0
                        ? "bg-[#54B0AF] hover:bg-teal-600"
                        : "bg-black"
                    }`}
                  >
                    {saving
                      ? "Saving..."
                      : currentPage < totalPages - 1
                      ? "Continue"
                      : "Finish Selection"}
                    {!saving && <ChevronRight size={18} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — USER TILE + PANDA TILE */}
          <div className="w-[30%] bg-[#111] p-4 flex flex-col gap-4">

            {/* USER TILE */}
            <div className="flex-1 bg-[#222] rounded-xl border border-[#555] p-4 relative">
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-28 h-28 rounded-full border-[3px] border-gray-300 overflow-hidden">
                  <img
                    src={user?.avatar || defaultUserAvatar}
                    alt={user?.name || "You"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="absolute bottom-3 left-3 bg-black/70 px-2 py-1 text-xs rounded text-white">
                {user?.name || "You"}
              </div>
            </div>

            {/* PANDA TILE */}
            <div className="flex-1 bg-[#222] rounded-xl border border-[#54B0AF] p-4 relative">
              <div className="w-full h-full flex items-center justify-center">
                <PandaWave />
              </div>
              <div className="absolute bottom-3 left-3 bg-black/70 px-2 py-1 text-xs rounded text-white">
                Sto – Career Companion
              </div>
            </div>
          </div>
        </div>

        {/* ZOOM TOOLBAR */}
        <div className="w-full bg-[#1C1C1C] py-3 px-10 flex items-center justify-between">

          {/* Left Icons */}
          <div className="flex items-center gap-10">
            {[
              [UnmuteIcon, "Mute", true],
              [StartVideoIcon, "Start Video", true],
              [SecurityShieldIcon, "Security", false],
              [ParticipantsIcon, "Participants", true],
              [ChatIcon, "Chat", false],
              [ShareScreenIcon, "Share Screen", true, "green"],
              [PollingIcon, "Polling", false],
              [RecordIcon, "Record", false],
              [BreakoutIcon, "Breakout Rooms", false],
              [ReactionIcon, "Reactions", false],
            ].map(([icon, label, showArrow, green], idx) => (
              <div
                key={idx}
                className={`flex flex-col items-center text-[11px] cursor-pointer ${
                  green ? "text-[#6CFF7F]" : "text-gray-200"
                }`}
              >
                <img src={icon} className="w-6 h-6 mb-1" alt="icon" />
                <span className={`${green ? "text-white" : ""}`}>{label}</span>
                {showArrow && <span className="text-[9px] -mt-1">⌄</span>}
              </div>
            ))}
          </div>

          <button className="bg-[#E64545] px-5 py-2 rounded-md text-white text-sm font-medium">
            End
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyBeliefs;
